import {
  cancelExchange,
  getExchangePrice,
  getInboundExchangeOffers,
  getInboundJourneys,
  getNonTripOffers,
  getOutboundExchangeOffers,
  getOutboundJourneys,
  getPayableAmount,
  getPurchaseFlowBookingAdditionalOffersSearch,
  getPurchaseFlowBookingById,
  getPurchaseFlowTravelAccountsMap,
  getSeatSelectionBookingById,
  resetPurchase,
  setSelectedInboundJourney,
  setSelectedInboundOfferMapByLegId,
  setSelectedOutboundJourney,
  setSelectedOutboundOfferMapByLegId,
  startExchange,
  updateSelectedInboundOfferMapByLegId,
  updateSelectedNonTripOffer,
  updateSelectedOutboundOfferMapByLegId,
} from '@/features/purchase/purchaseActions';
import type {
  BookingDTO,
  SearchJourneyOffersDTO,
  SearchNonTripOfferDTO,
  SearchNonTripOffersDTO,
  SearchOffersDTO,
  StartExchangeOperationResponseDTO,
} from '@/types/dto/agent';
import type {
  AdditionalOffersCollection,
  OfferMapByLegId,
} from '@/types/offer';
import type { TravelAccountsMap } from '@/types/travelpass';
import { orderBookingTripsBasedOnDepartureTime } from '@/utils/booking';
import { createReducer } from '@reduxjs/toolkit';

type SearchOffers = SearchOffersDTO & {
  selectedJourney?: SearchJourneyOffersDTO;
  selectedOfferMapByLegId: OfferMapByLegId;
  additionalOffers: Array<AdditionalOffersCollection>;
};

type SearchNonTripOffers = SearchNonTripOffersDTO & {
  selectedNonTripOffer?: SearchNonTripOfferDTO;
};

type PurchaseState = {
  outbound: SearchOffers;
  inbound: SearchOffers;
  nonTripOffers: SearchNonTripOffers;
  booking: BookingDTO | null;
  travelAccountsMap?: TravelAccountsMap;
  exchangePrice: number | null;
  // Authoritative "amount to be paid" for the change-booking checkout's
  // "Change total" — from `Bookings_GetBookingPayableAmount`.
  payableAmount: number | null;
  // Lightweight PREBOOKED op (id + status) set by `startExchange`. The full
  // operation lives on `manage.booking.exchangeOperations[]` after refetch.
  exchangeOperation: StartExchangeOperationResponseDTO | null;
};

const initialState: PurchaseState = {
  outbound: {
    selectedOfferMapByLegId: {},
    additionalOffers: [],
  },
  inbound: {
    selectedOfferMapByLegId: {},
    additionalOffers: [],
  },
  nonTripOffers: {},
  booking: null,
  exchangePrice: null,
  payableAmount: null,
  exchangeOperation: null,
};

export const purchaseReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(getOutboundJourneys.fulfilled, (state, action) => {
      state.outbound = { ...state.outbound, ...action.payload };
    })
    .addCase(getInboundJourneys.fulfilled, (state, action) => {
      state.inbound = { ...state.inbound, ...action.payload };
    })
    .addCase(getOutboundExchangeOffers.fulfilled, (state, action) => {
      state.outbound = { ...state.outbound, ...action.payload };
    })
    .addCase(getInboundExchangeOffers.fulfilled, (state, action) => {
      state.inbound = { ...state.inbound, ...action.payload };
    })
    // Set `journeys: []` on rejection so the consuming useEffect in the
    // journey lists doesn't re-dispatch the failing call in a loop. The
    // .catch on .unwrap() still toasts the BE error.
    .addCase(getOutboundJourneys.rejected, (state) => {
      state.outbound = { ...state.outbound, journeys: [] };
    })
    .addCase(getInboundJourneys.rejected, (state) => {
      state.inbound = { ...state.inbound, journeys: [] };
    })
    .addCase(getOutboundExchangeOffers.rejected, (state) => {
      state.outbound = { ...state.outbound, journeys: [] };
    })
    .addCase(getInboundExchangeOffers.rejected, (state) => {
      state.inbound = { ...state.inbound, journeys: [] };
    })
    .addCase(getExchangePrice.fulfilled, (state, action) => {
      // Guard non-finite values (NaN, ±Infinity) from a malformed BE response → footer renders '-'.
      state.exchangePrice = Number.isFinite(action.payload)
        ? action.payload
        : null;
    })
    .addCase(getExchangePrice.rejected, (state) => {
      state.exchangePrice = null;
    })
    .addCase(getPayableAmount.fulfilled, (state, action) => {
      state.payableAmount = Number.isFinite(action.payload)
        ? action.payload
        : null;
    })
    .addCase(getPayableAmount.rejected, (state) => {
      state.payableAmount = null;
    })
    .addCase(startExchange.fulfilled, (state, action) => {
      state.exchangeOperation = action.payload;
    })
    .addCase(startExchange.rejected, (state) => {
      state.exchangeOperation = null;
    })
    .addCase(cancelExchange.fulfilled, (state) => {
      state.exchangeOperation = null;
    })
    .addCase(getNonTripOffers.fulfilled, (state, action) => {
      state.nonTripOffers = { ...state.nonTripOffers, ...action.payload };
    })
    .addCase(setSelectedOutboundJourney, (state, action) => {
      state.outbound.selectedJourney = action.payload;
    })
    .addCase(setSelectedOutboundOfferMapByLegId, (state, action) => {
      state.outbound.selectedOfferMapByLegId = action.payload;
    })
    .addCase(updateSelectedOutboundOfferMapByLegId, (state, action) => {
      state.outbound.selectedOfferMapByLegId[action.payload.legId] =
        action.payload.offer;
    })
    .addCase(updateSelectedNonTripOffer, (state, action) => {
      state.nonTripOffers.selectedNonTripOffer = action.payload;
    })
    .addCase(setSelectedInboundJourney, (state, action) => {
      state.inbound.selectedJourney = action.payload;
    })
    .addCase(setSelectedInboundOfferMapByLegId, (state, action) => {
      state.inbound.selectedOfferMapByLegId = action.payload;
    })
    .addCase(updateSelectedInboundOfferMapByLegId, (state, action) => {
      state.inbound.selectedOfferMapByLegId[action.payload.legId] =
        action.payload.offer;
    })
    .addCase(resetPurchase, (state) => {
      Object.assign(state, initialState);
    })
    .addCase(
      getPurchaseFlowBookingAdditionalOffersSearch.fulfilled,
      (state, action) => {
        state.outbound.additionalOffers =
          action.payload.outboundAdditionalOffers;
        state.inbound.additionalOffers = action.payload.inboundAdditionalOffers;
      }
    )
    .addCase(getPurchaseFlowTravelAccountsMap.fulfilled, (state, action) => {
      state.travelAccountsMap = action.payload;
    })
    .addMatcher(
      (action) =>
        [
          getPurchaseFlowBookingById.fulfilled.type,
          getSeatSelectionBookingById.fulfilled.type,
        ].includes(action.type),
      (state, action) => {
        state.booking = orderBookingTripsBasedOnDepartureTime(action.payload);
      }
    );
});
